/**
 * Shared CDN bundle import used by both the module loader and the view loader.
 *
 * Views and modules are the same delivery mechanism — a hashed ES-module bundle
 * on the CDN, dynamically `import()`ed in the browser — differing only in the
 * path segment (`views/` vs `modules/`) and the export they validate. This
 * module owns the one piece they genuinely share: performing the dynamic import
 * and classifying a failure as network vs. import/evaluation. Each loader then
 * re-wraps the classified failure into its own domain error (`ModuleLoadError`
 * / `ViewLoadError`) with the id/version/url context it owns.
 */

export type BundleImportFn = (
  url: string,
) => Promise<Record<string, unknown>>;

/** Native dynamic import. `@vite-ignore` keeps Vite from trying to pre-bundle a runtime CDN URL. */
export const importBundleModule: BundleImportFn = (url) =>
  import(/* @vite-ignore */ url);

export type BundleImportErrorCode = "NETWORK_ERROR" | "IMPORT_ERROR";

/**
 * Classified failure of a dynamic bundle import.
 *
 * The originating error is carried on the standard `cause` (ES2022) rather than
 * a bespoke field, so it survives across `Error` tooling; callers read it back
 * with an `instanceof Error` guard.
 */
export class BundleImportError extends Error {
  readonly code: BundleImportErrorCode;

  constructor(code: BundleImportErrorCode, options: { cause: Error }) {
    super(options.cause.message, options);
    this.name = "BundleImportError";
    this.code = code;
  }
}

/**
 * A network-level failure surfaces as a `TypeError` from `fetch` inside the
 * dynamic import; anything else (syntax error, missing export, evaluation
 * throw) is an import error.
 */
function isNetworkError(err: Error): boolean {
  return err instanceof TypeError && err.message.includes("fetch");
}

/**
 * Dynamically import a bundle from `url`, normalizing any thrown value into a
 * classified {@link BundleImportError}.
 */
export async function importBundle(
  url: string,
  importFn: BundleImportFn = importBundleModule,
): Promise<Record<string, unknown>> {
  try {
    return await importFn(url);
  } catch (err) {
    const cause = err instanceof Error ? err : new Error(String(err));
    throw new BundleImportError(
      isNetworkError(cause) ? "NETWORK_ERROR" : "IMPORT_ERROR",
      { cause },
    );
  }
}
